import { FC, useCallback, useState } from 'react';

import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Box,
  TablePagination,
  debounce,
  CircularProgress,
  useTheme,
} from '@mui/material';
import moment from 'moment';

import { SearchBar } from '@/components';
import { FilterMenu } from '@/components/Modals/UnitShiftListModal/components/LogsFilter';
import { DATE_TIME_FORMAT } from '@/config';
import { IUnit } from '@/models';
import { formatPersonName } from '@/utils';

interface ShiftLogsTableProps {
  count: number;
  data: any[];
  onPageChange: (
    page: number,
    rowsPerPage: number,
    activeFilter?: any,
    activeSearchTerm?: string,
  ) => void;
  onSearch: (value: string) => void;
  onRowsPerPageChange: (
    page: number,
    rowsPerPage: number,
    activeFilter?: any,
    activeSearchTerm?: string,
  ) => void;
  rowsPerPage: number;
  page: number;
  units?: IUnit[];
  onFilter?: (page: number, limit: number, filter: any) => void;
  isLoading?: boolean;
  activeLogsFilter?: any;
  activeSearchTerm?: string;
}

const columns = ['Created at', 'Event', 'Unit', 'Changed by'];

enum CFSEventMapper {
  CLOSED = 'Closed',
  UNIT_SHIFT_ASSIGNED = 'Assigned',
  UNIT_SHIFT_IS_EN_ROUTE = 'En Route',
  UNIT_SHIFT_IS_ON_SCENE = 'On Scene',
  UNIT_SHIFT_IS_IN_PROGRESS = 'In Progress',
  UNIT_SHIFT_CANCELED = 'Canceled',
  UNIT_SHIFT_COMPLETED = 'Completed',
  UNIT_SHIFT_IS_IN_THE_AREA = 'In The Area',
}

const ShiftLogsTableRow: FC<{ row: any }> = ({ row }) => {
  return (
    <>
      <TableRow sx={{ height: '45px' }}>
        <TableCell>{moment(row?.createdAt).format(DATE_TIME_FORMAT)}</TableCell>
        <TableCell>
          <Box
            sx={{
              backgroundColor: '#F3F4F6',
              padding: '4px 12px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              fontSize: '0.875rem',
              color: '#1F2937',
              width: '90px',
              height: '32px',
            }}
          >
            {CFSEventMapper[row.event as keyof typeof CFSEventMapper]}
          </Box>
        </TableCell>

        <TableCell>{row.data?.new?.unit?.name}</TableCell>
        <TableCell>
          {row.createdByUser
            ? formatPersonName(row.createdByUser)
            : formatPersonName(row.createdBy)}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={columns.length} padding="none">
          <Divider />
        </TableCell>
      </TableRow>
    </>
  );
};

export default function UnitShiftLogs({
  count,
  data,
  onPageChange,
  onSearch,
  onRowsPerPageChange,
  rowsPerPage,
  page,
  units,
  onFilter,
  isLoading = false,
  activeLogsFilter,
  activeSearchTerm,
}: ShiftLogsTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [, setIsSearching] = useState(false);
  const theme = useTheme();

  const debouncedSearch = useCallback(
    debounce(async (term: string) => {
      setIsSearching(true);
      try {
        await onSearch(term);
      } finally {
        setIsSearching(false);
      }
    }, 500),
    [onSearch],
  );

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const onChangeFilter = (filterData: any) => {
    if (onFilter) {
      onFilter(0, rowsPerPage, filterData);
    }
  };

  return (
    <Stack spacing={2} flex={1} marginBottom="2rem" margin={2}>
      <Box sx={{ width: '100%', marginBottom: '1.5rem', position: 'relative' }}>
        <SearchBar
          onChange={handleSearchChange}
          value={searchTerm}
          shouldCancel=""
          sx={{
            width: '92% !important',
            maxWidth: '92% !important',
            backgroundColor: theme.palette.background.paper,
            '& .MuiOutlinedInput-root': {
              width: '100%',
              backgroundColor: theme.palette.background.paper,
              paddingRight: '48px',
            },
            '& .MuiInputBase-root': {
              width: '100%',
            },
            '& .MuiInputBase-input': {
              width: '100%',
              padding: '12px 14px',
            },
          }}
          placeholder="Search..."
        />
        <Box
          sx={{
            position: 'absolute',
            right: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <FilterMenu onChangeFilter={onChangeFilter} tableData={units} />
        </Box>
      </Box>

      <TableContainer
        sx={{ maxHeight: 'calc(100vh - 300px)', overflow: 'auto' }}
      >
        <Table stickyHeader aria-label="sticky table">
          <TableHead
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              bgcolor: 'background.paper',
            }}
          >
            <TableRow>
              {columns.map((title) => (
                <TableCell
                  key={title}
                  sx={{
                    fontWeight: 600,
                    fontSize: '1rem',
                    padding: '24px',
                  }}
                >
                  {title}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell
                colSpan={columns.length}
                sx={{
                  backgroundColor: 'transparent',
                  padding: '0px !important',
                }}
              >
                <Divider />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  align="center"
                  colSpan={columns.length}
                  sx={{
                    color: 'grey',
                    borderBottomWidth: 0,
                    padding: '2rem',
                  }}
                >
                  <CircularProgress size={32} sx={{ color: '#6B7280' }} />
                </TableCell>
              </TableRow>
            ) : data.length > 0 ? (
              data.map((row, index) => (
                <ShiftLogsTableRow key={index} row={row} />
              ))
            ) : (
              <TableRow>
                <TableCell
                  align="center"
                  colSpan={columns.length}
                  sx={{
                    color: 'grey',
                    borderBottomWidth: 0,
                  }}
                >
                  No Data
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ overflow: 'none' }}>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => {
            onPageChange(
              newPage,
              rowsPerPage,
              activeLogsFilter,
              activeSearchTerm,
            );
          }}
          onRowsPerPageChange={(event) => {
            const newRowsPerPage = parseInt(event.target.value, 10);
            onRowsPerPageChange(
              0,
              newRowsPerPage,
              activeLogsFilter,
              activeSearchTerm,
            );
          }}
        />
      </Box>
    </Stack>
  );
}
